import axios from "axios";
import { PropsWithChildren, createContext, useContext, useEffect, useState } from "react";

const BACKEND_TASK_STATUS_INTERVAL_MS = 10 * 1000; // 10s

export interface IBackendTaskStatus {
  task_id: string;
  status: "PENDING" | "STARTED" | "RETRY" | "FAILURE" | "SUCCESS";
  successful: boolean;
  ready: boolean;
  result: string;
}

const BackendTaskStatusContext = createContext<{
  subscribe: (taskId: string, callback: (status: IBackendTaskStatus) => void) => void;
  unsubscribe: (taskId: string, callback: (status: IBackendTaskStatus) => void) => void;
  watchTask: (taskId: string, onSuccess: () => void, onError?: () => void) => void;
}>({
  subscribe: () => {},
  unsubscribe: () => {},
  watchTask: () => {},
});

const taskCallbackMap = new Map<string, Function[]>();
/** Подписка на изменение статуса задачи */
const subscribe = (taskId: string, callback: (status: IBackendTaskStatus) => void) => {
  taskCallbackMap.set(taskId, [...(taskCallbackMap.get(taskId) || []), callback]);
};

const notify = (taskId: string, status: IBackendTaskStatus) => {
  taskCallbackMap.get(taskId)?.forEach((callback) => callback(status));
};
/** Отписка от изменений статуса задачи. Вызывать вручную */
const unsubscribe = (taskId: string, callback: (status: IBackendTaskStatus) => void) => {
  const callbacks = taskCallbackMap.get(taskId)?.filter((cb) => cb !== callback);
  if (!callbacks?.length) {
    taskCallbackMap.delete(taskId);
  } else {
    taskCallbackMap.set(taskId, callbacks);
  }
};

/** Шаблон для подписки на изменение статуса задачи */
const watchTask = (taskId: string, onSuccess: () => void, onError?: () => void) => {
  const callback = (status: IBackendTaskStatus) => {
    if (status.status === "SUCCESS") {
      unsubscribe(taskId, callback);
      onSuccess();
    }
    if (status.status === "FAILURE") {
      unsubscribe(taskId, callback);
      onError?.();
    }
  };
  subscribe(taskId, callback);
};

const BackendTaskStatusProvider = ({ children }: PropsWithChildren) => {
  const [subscribersCount, setSubscribersCount] = useState(0);
  const needCheckTaskStatus = subscribersCount > 0;

  useEffect(() => {
    if (!needCheckTaskStatus) {
      return;
    }
    let timeoutId: NodeJS.Timeout;
    const checkTaskStatus = async () => {
      if (!taskCallbackMap.size) {
        timeoutId = setTimeout(checkTaskStatus, BACKEND_TASK_STATUS_INTERVAL_MS);
        return;
      }
      try {
        const results = await Promise.allSettled(
          Array.from(taskCallbackMap.keys()).map((taskId) =>
            axios.get<IBackendTaskStatus>(`/help/celery-task/${taskId}/`)
          )
        );

        results.forEach((result) => {
          if (result.status === "fulfilled") {
            notify(result.value.data.task_id, result.value.data);
          }
        });
      } finally {
        timeoutId = setTimeout(checkTaskStatus, BACKEND_TASK_STATUS_INTERVAL_MS);
      }
    };

    checkTaskStatus();

    return () => {
      clearTimeout(timeoutId);
    };
  }, [needCheckTaskStatus]);

  const incrementSubscribersCount = () => {
    setSubscribersCount((prev) => Math.max(prev + 1, 0));
  };

  const decrementSubscribersCount = () => {
    setSubscribersCount((prev) => Math.max(prev - 1, 0));
  };

  const handleSubscribe = (taskId: string, callback: (status: IBackendTaskStatus) => void) => {
    subscribe(taskId, callback);
    incrementSubscribersCount();
  };

  const handleUnsubscribe = (taskId: string, callback: (status: IBackendTaskStatus) => void) => {
    unsubscribe(taskId, callback);
    decrementSubscribersCount();
  };

  const handleWatchTask = (taskId: string, onSuccess: () => void, onError?: () => void) => {
    watchTask(
      taskId,
      () => {
        onSuccess();
        decrementSubscribersCount();
      },
      () => {
        onError?.();
        decrementSubscribersCount();
      }
    );
    incrementSubscribersCount();
  };

  return (
    <BackendTaskStatusContext.Provider
      value={{ subscribe: handleSubscribe, unsubscribe: handleUnsubscribe, watchTask: handleWatchTask }}
    >
      {children}
    </BackendTaskStatusContext.Provider>
  );
};

export const useBackendTaskStatus = () => useContext(BackendTaskStatusContext);

export default BackendTaskStatusProvider;
